import { useMutation, useQueryClient } from '@tanstack/react-query';

import { copyGlossaryService, copySpacesService, duplicateContentService } from '@/services/httpServices/AdminServices';
import type { CopyGlossaryServiceConfig, CopySpacesServiceConfig, DuplicateContentServiceConfig } from '@/services/httpServices/AdminServices/types';

import { quizListKeys } from '../queries';
import { courseListKeys } from '../queries/courseQueryKeys';
import { sequenceListKeys } from '../queries/sequenceQueryKeys';
import { infiniteSpacesListKeys, spacesListKeys } from '../queries/spaceQueryKeys';

export function useDuplicateContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DuplicateContentServiceConfig) => {
      try {
        const response = await duplicateContentService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate relevant queries based on content type
      const contentType = variables.params?.body.contentType;

      if (contentType === 'quiz') {
        await queryClient.invalidateQueries({
          queryKey: quizListKeys(),
        });
      } else if (contentType === 'sequence') {
        await queryClient.invalidateQueries({
          queryKey: sequenceListKeys(),
        });
      } else if (contentType === 'course') {
        // Invalidate course lists when courses are duplicated using hierarchical keys
        await queryClient.invalidateQueries({
          queryKey: courseListKeys(),
        });
      }
    },
  });
}

export function useCopySpacesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CopySpacesServiceConfig) => {
      try {
        return await copySpacesService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to copy spaces',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate both the plain and the infinite spaces-list queries so the
      // new spaces show up in the switcher without a manual refresh. The
      // switcher uses the infinite query (key ['infinite', 'spacesList', ...]),
      // which prefix-matching from spacesListKeys() alone would not catch.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: spacesListKeys() }),
        queryClient.invalidateQueries({ queryKey: infiniteSpacesListKeys() }),
        // Refresh permissions for the new spaces.
        queryClient.invalidateQueries({ queryKey: ['userRole'] }),
      ]);
    },
  });
}

export function useCopyGlossaryMutation() {
  return useMutation({
    mutationFn: async (config: CopyGlossaryServiceConfig) => {
      try {
        return await copyGlossaryService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to copy glossary',
        );
      }
    },
  });
}
